import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useUser } from './UserContext';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'halfmeyer-theme';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};

function readStoredTheme(): Theme | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem('theme');
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    /* ignore */
  }
  return null;
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, darkMode } = useUser();
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = readStoredTheme();
    if (stored) return stored;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });

  useEffect(() => {
    if (user && typeof darkMode === 'boolean') {
      setTheme(darkMode ? 'dark' : 'light');
    }
  }, [user, darkMode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.classList.remove('dark');
    try {
      localStorage.setItem(STORAGE_KEY, theme);
      localStorage.setItem('theme', theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    const previousTheme = theme;

    setTheme(newTheme);

    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { darkMode: newTheme === 'dark' });
      } catch (error) {
        console.error('Failed to update darkMode in Firestore:', error);
        setTheme(previousTheme);
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
  );
};
